import { Injectable } from '@angular/core';
import {
  EmailListItemContract,
  EmailSearchByWidgetRequest,
  EmailSearchRequest,
  TransformEmailListItemContract,
} from '@app-types/api/email';
import { MAIL_ACTIONS } from '@app-shared/constants/constants';
import { EmailColumnsEnum } from '@app-types/enums/emailColumns';
import { MailFolderType } from '@app-types/enums/mail-folder.type';
import { EmailValidationState } from '@app-types/enums/email.validation-state';
import { EmailState } from '@app-types/enums/email-state';
import { EmailsScrollService } from '@app-services/emails/emails-scroll.service';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { MatchError } from '@app-services/errors/error-matcher';
import { EmailSearchOrder } from '@app-types/enums/email.search-order';
import { EmailClient } from '@app-services/api/clients/email-client';
import { EmailsColumnsService } from '@app-services/emails/emails-columns.service';
import { TransformDataTableService } from '@app-services/emails/transform-data-table.service';
import { EmailsPageStateService } from '@app-services/emails/emails-page-state.service';
import { FormatSearchOrders } from '@app-pipes/format-search-orders.pipe';

@Injectable({
  providedIn: 'root',
})
export class EmailsService {
  public formatSearchOrders = new FormatSearchOrders();

  constructor(
    private emailsScrollService: EmailsScrollService,
    private userWorkspaceService: UserWorkspaceService,
    public readonly matchError: MatchError,
    private emailClient: EmailClient,
    private emailsColumnsService: EmailsColumnsService,
    private transformDataTableService: TransformDataTableService,
    private emailsPageStateService: EmailsPageStateService
  ) {}

  getStyle(folderType: MailFolderType): string {
    return folderType !== MailFolderType.Sent &&
      folderType !== MailFolderType.Outbox &&
      folderType !== MailFolderType.Archive &&
      folderType !== MailFolderType.Trash &&
      !this.emailsPageStateService.isShowFolderColumn()
      ? folderType === MailFolderType.View
        ? 'right-menu-long'
        : 'right-menu-narrow'
      : 'right-menu-wide';
  }

  setInitState(
    folderType: MailFolderType,
    canFinalDelete: boolean,
    reassigned: boolean | null
  ): void {
    this.emailsPageStateService.updateConfigPage({
      isFirstRowTo: this.emailsColumnsService.isColumnShow(
        folderType,
        false,
        true
      ),
      isFirstRowFrom: this.emailsPageStateService.selectedMailFolderId()
        ? this.emailsColumnsService.isColumnShow(folderType, true, true)
        : true,
      hasSourceColumn:
        folderType === MailFolderType.Archive ||
        folderType === MailFolderType.Trash,
      isMailFolderTypeOutbox: folderType === MailFolderType.Outbox,
      styleContainerHoverMenu: this.getStyle(folderType),
      multipleReassignIsAvailable:
        !!this.emailsPageStateService.selectedMailFolderId() &&
        folderType === MailFolderType.Inbox &&
        !reassigned,
      isImportEML:
        folderType === MailFolderType.Inbox ||
        folderType === MailFolderType.Sent,
      isShowFolderColumn:
        this.emailsPageStateService.selectedMailFolderIds()?.length > 1 ||
        folderType === MailFolderType.View,
      canFinalDelete,
    });
  }

  setColumns(mailFolderType: MailFolderType): void {
    this.emailsPageStateService.displayedColumns =
      this.emailsColumnsService.getColumns(
        mailFolderType,
        this.emailsPageStateService.selectedMailFolderId(),
        this.emailsPageStateService.isMailFolderTypeOutbox(),
        this.emailsPageStateService.isShowFolderColumn()
      );
  }

  addColumns(): void {
    const indexFrom = this.emailsPageStateService.displayedColumns.indexOf(
      EmailColumnsEnum.From
    );
    const indexTo = this.emailsPageStateService.displayedColumns.indexOf(
      EmailColumnsEnum.To
    );
    const indexPreColumn = indexFrom === -1 ? indexTo : indexFrom;
    if (
      !this.emailsPageStateService.displayedColumns.includes(
        EmailColumnsEnum.Reply
      )
    ) {
      const hasReply = this.emailsPageStateService.dataSource.data.some(
        e => !!e?.replyType
      );
      hasReply &&
        this.emailsPageStateService.displayedColumns.splice(
          indexPreColumn,
          0,
          EmailColumnsEnum.Reply
        );
    }

    if (
      !this.emailsPageStateService.displayedColumns.includes(
        EmailColumnsEnum.Attachment
      )
    ) {
      const indexReply = this.emailsPageStateService.displayedColumns.indexOf(
        EmailColumnsEnum.Reply
      );
      const hasAttachments = this.emailsPageStateService.dataSource.data.some(
        e => e.hasAttachments
      );
      hasAttachments &&
        this.emailsPageStateService.displayedColumns.splice(
          indexReply === -1 ? indexPreColumn : indexReply + 1,
          0,
          EmailColumnsEnum.Attachment
        );
    }
  }

  public async getFolderType(): Promise<void> {
    let searchOrders = ['date', 'from', 'to'].map(key => ({
      id: this.formatSearchOrders.transform(key),
      name: key,
    }));

    if (
      this.emailsPageStateService.infoOpenedMailAccount.type ===
        MailFolderType.Inbox ||
      this.emailsPageStateService.infoOpenedMailAccount.type ===
        MailFolderType.Spam
    ) {
      searchOrders = searchOrders.filter(
        e => e.id !== EmailSearchOrder.OrderByTo
      );
    }
    if (
      this.emailsPageStateService.infoOpenedMailAccount.type ===
        MailFolderType.Validation ||
      this.emailsPageStateService.infoOpenedMailAccount.type ===
        MailFolderType.Draft ||
      this.emailsPageStateService.infoOpenedMailAccount.type ===
        MailFolderType.Outbox
    ) {
      if (
        this.emailsPageStateService.searchOrder ===
          EmailSearchOrder.OrderBySentDateTime ||
        this.emailsPageStateService.searchOrder ===
          EmailSearchOrder.OrderByStateDateTime
      ) {
        this.emailsPageStateService.searchOrder =
          EmailSearchOrder.OrderByStateDateTime;
      }
      searchOrders = searchOrders.map(e =>
        e.id === EmailSearchOrder.OrderBySentDateTime
          ? {
              ...e,
              id: EmailSearchOrder.OrderByStateDateTime,
            }
          : e
      );
    }

    if (
      this.emailsPageStateService.infoOpenedMailAccount.type &&
      this.emailsPageStateService.infoOpenedMailAccount.type >=
        MailFolderType.Sent &&
      this.emailsPageStateService.infoOpenedMailAccount?.type <=
        MailFolderType.Validation
    ) {
      searchOrders = searchOrders.filter(
        e => e.id !== EmailSearchOrder.OrderByFrom
      );
    }

    if (
      this.emailsPageStateService.infoOpenedMailAccount?.type &&
      this.emailsPageStateService.infoOpenedMailAccount.type ===
        MailFolderType.Trash
    ) {
      searchOrders = searchOrders.map(e =>
        e.id === EmailSearchOrder.OrderBySentDateTime
          ? {
              ...e,
              id: EmailSearchOrder.OrderBySentDateTimeOrStateDateTimeIfSentDateTimeIsNull,
            }
          : e
      );
      if (
        this.emailsPageStateService.searchOrder ===
        EmailSearchOrder.OrderBySentDateTime
      ) {
        this.emailsPageStateService.searchOrder =
          EmailSearchOrder.OrderBySentDateTimeOrStateDateTimeIfSentDateTimeIsNull;
      }
    }
    this.emailsPageStateService.searchOrders = searchOrders;
  }

  getTransformDataEmails(
    data: EmailListItemContract[]
  ): TransformEmailListItemContract[] {
    return this.transformDataTableService.transformData(
      data,
      this.emailsPageStateService.selectedMailFolderId(),
      this.emailsPageStateService.isShowFolderColumn(),
      this.emailsPageStateService.hasSourceColumn(),
      this.emailsPageStateService.searchParams.reassigned,
      this.emailsPageStateService.canFinalDelete(),
      this.emailsPageStateService.infoOpenedMailAccount.type,
      this.emailsPageStateService.infoOpenedMailAccount.acc
    );
  }

  async getEmails(getCount: boolean): Promise<void> {
    this.emailsScrollService.updateScrollConfig({
      canNavigateForward: false,
    });
    try {
      const hasWidget =
        this.emailsPageStateService.searchWidgetId &&
        this.emailsPageStateService.searchWidgetId > 0;
      const offset = this.emailsScrollService.offset();
      const request = {
        offset: offset === 0 ? offset : offset - 1,
        fetch: this.emailsScrollService.stepFetch,
        mailFolderIds: this.emailsPageStateService.selectedMailFolderIds(),
      };
      !hasWidget &&
        getCount &&
        ((request as EmailSearchRequest).getCount = getCount);
      hasWidget
        ? ((request as EmailSearchByWidgetRequest).searchWidgetId =
            this.emailsPageStateService.searchWidgetId)
        : ((request as EmailSearchRequest).filter =
            this.emailsPageStateService.searchParams);

      const result = hasWidget
        ? await this.emailClient.searchByWidget(
            request as EmailSearchByWidgetRequest
          )
        : await this.emailClient.search(request as EmailSearchRequest);

      !hasWidget &&
        (this.emailsPageStateService.counts = getCount
          ? result.counts
          : this.emailsPageStateService.counts);

      if (this.emailsScrollService.offset() === 0) {
        const newDataTable = this.getTransformDataEmails(
          result.data.slice(0, this.emailsScrollService.pageSize)
        );
        this.emailsPageStateService.setDataTable(newDataTable);
      } else {
        const lastEmail =
          this.emailsPageStateService.dataSource.data[
            this.emailsPageStateService.dataSource.data.length - 1
          ];
        const index = result.data.findIndex(
          el => el.emailId === lastEmail?.emailId
        );
        const newEmails = result.data.slice(
          index === -1 ? 0 : index + 1,
          index === -1
            ? this.emailsScrollService.pageSize
            : index + 1 + this.emailsScrollService.pageSize
        );
        const nextData = this.getTransformDataEmails(newEmails);
        this.emailsPageStateService.addDataTable(nextData);
      }
      this.emailsScrollService.updateScrollConfig({
        canNavigateForward:
          result.data &&
          result.data.length >= this.emailsScrollService.pageSize,
      });

      this.emailsPageStateService.selectedMailFolderId() && this.addColumns();
    } catch (e) {
      if (this.emailsScrollService.isLoadingScroll()) {
        this.emailsScrollService.updateScrollConfig({
          isErrorScroll: true,
        });
      } else {
        this.emailsPageStateService.isError = true;
        this.emailsPageStateService.setDataTable([]);
      }
      this.matchError.logError(e);
    }
  }

  async loadData(
    getFolders: boolean = false,
    getCount: boolean = false,
    showLoader: boolean = false
  ): Promise<void> {
    const offset = this.emailsScrollService.offset();
    const loaderTimeout =
      !offset && !this.emailsPageStateService.dataSource.data.length
        ? setTimeout(() => {
            !showLoader &&
              !this.emailsPageStateService.showLoader &&
              (this.emailsPageStateService.showLoader = true);
          }, 500)
        : undefined;
    showLoader && (this.emailsPageStateService.showLoader = true);

    this.emailsPageStateService.isError = false;

    this.emailsScrollService.updateScrollConfig({
      isErrorScroll: false,
      isLoadingScroll: !!offset,
    });

    try {
      getFolders && (await this.getFolderType());
      this.emailsPageStateService.searchParams.searchOrder =
        this.emailsPageStateService.searchOrder;
      this.emailsPageStateService.searchParams.orderDirection =
        this.emailsPageStateService.orderDirection;
      this.emailsPageStateService.searchParams.seen =
        this.emailsPageStateService.searchParams.seen ??
        this.emailsPageStateService.onlyUnseen;

      const isAllSelected = this.isAllSelected;
      await this.getEmails(getCount);
      isAllSelected && this.selectAllRows();
    } catch (e) {
      if (this.emailsScrollService.isLoadingScroll()) {
        this.emailsScrollService.updateScrollConfig({
          isErrorScroll: true,
        });
      } else {
        this.emailsPageStateService.isError = true;
      }
      this.matchError.logError(e);
    } finally {
      loaderTimeout && clearTimeout(loaderTimeout);
      this.emailsPageStateService.showLoader &&
        (this.emailsPageStateService.showLoader = false);
      this.emailsScrollService.isLoadingScroll() &&
        this.emailsScrollService.updateScrollConfig({
          isLoadingScroll: false,
        });
    }
  }

  async onScroll(): Promise<void> {
    this.emailsScrollService.setNextOffset();
    await this.loadData();
    this.emailsScrollService.resetScroll();
  }

  // methods for selection

  get isAllSelected(): boolean {
    const lengthSelected =
      this.emailsPageStateService.selection?.selected?.length;
    const lengthDataSource =
      this.emailsPageStateService.dataSource?.data?.length;

    if (!lengthSelected || !lengthDataSource) {
      return false;
    }
    return lengthSelected === lengthDataSource;
  }

  selectAllRows(): void {
    this.emailsPageStateService.dataSource.data.forEach(row =>
      this.emailsPageStateService.selection.select(row.emailId)
    );
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle(): void {
    this.isAllSelected
      ? this.emailsPageStateService.selection.clear()
      : this.selectAllRows();
  }

  get selectedCount(): number {
    return this.emailsPageStateService.selection.selected.length;
  }

  get oneSelected(): boolean {
    return this.selectedCount === 1;
  }

  public get validateIsAvailable(): boolean {
    return this.selectedEmails.every(
      e =>
        e.validationState === EmailValidationState.New ||
        e.validationState === EmailValidationState.NotValidated
    );
  }

  get selectedEmails(): TransformEmailListItemContract[] {
    return this.emailsPageStateService.dataSource.data.filter(email =>
      this.emailsPageStateService.selection.isSelected(email.emailId)
    );
  }

  getCommonActionButtons(
    selectedEmails: TransformEmailListItemContract[]
  ): string[] {
    if (selectedEmails.length === 0) return [];
    return [...MAIL_ACTIONS].filter(action =>
      selectedEmails.every(email => email.actionButtons.includes(action))
    );
  }

  get commonActionButtons(): string[] {
    return this.getCommonActionButtons(this.selectedEmails);
  }

  public get isSelectedRetrySendingNeededState(): boolean {
    return this.selectedEmails.every(e => e.state === EmailState.SendError);
  }

  // reset update reload retryData

  async retryData(): Promise<void> {
    await this.loadData(true, false, true);
    setTimeout(() => {
      this.emailsScrollService.reconnect();
    }, 1000);
  }

  async reload(): Promise<void> {
    this.emailsPageStateService.isReloadLoading = true;
    this.emailsPageStateService.selection.clear();
    this.emailsScrollService.resetOffset();
    this.emailsScrollService.scrollToTop();
    await this.loadData(true, true);
    this.emailsPageStateService.infoOpenedMailAccount.acc &&
      (await this.userWorkspaceService.refreshUnread({
        mailAccountId: this.emailsPageStateService.infoOpenedMailAccount.acc,
      }));
    this.emailsPageStateService.isReloadLoading = false;
  }

  // public reloadDataOnDemand(): void {
  //   if (!this.emailsPageStateService.dataSource.data.length) {
  //     this.reload();
  //   }
  // }

  updateDataOnEmailCountChange(ids: number[]): void {
    this.emailsPageStateService.filterDataTable(ids);
    this.emailsPageStateService.selection.clear();
    const newCount =
      (this.emailsPageStateService.counts?.totalCount ?? 0) - ids.length;
    this.emailsPageStateService.counts = {
      ...(this.emailsPageStateService?.counts ?? {}),
      totalCount: newCount <= 0 ? 0 : newCount,
    };

    const newOffset = this.emailsScrollService.offset() - ids.length;
    this.emailsScrollService.updateScrollConfig({
      offset: newOffset <= 0 ? 0 : newOffset,
    });
  }
}
